import { QuoteType } from "@/lib/interfaces and Enums/ENUMQuoteType";
import { ITradeSettingsStore } from "@/store/tradeSettingsStore";
import { resolveSettingForCP } from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/SelectPanelHelper";
import {
  ICPSettingsOverride,
  IToggleButton,
} from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/tradeSettingsInterfaces";
import { SINGLE_CP_COVER_TOOLTIP } from "@/components/widgets/tradeActionSummary/helper/tradeSettingsHelper/coverSettingsRule";
import type { FontSizeSetting } from "@/components/widgets/tradeActionSummary/ActionSummaryOP/selectPanel/CPSettingsGridConstants";

// LQC-2271 - the condensed sidebar grid gained Side + Direction, so its columns
// are no longer a fixed set and the CSS grid template has to be built at render
// time. These pure helpers own the column list, the template and the minimum
// width the row needs before the panel must scroll horizontally.

export type CondensedSettingField = keyof ICPSettingsOverride &
  keyof ITradeSettingsStore;

export interface ICondensedColumn {
  settingField: CondensedSettingField;
  heading: string;
  // Floor width. Columns grow to fill a wide panel and stop here on a narrow
  // one, at which point the grid overflows and the panel scrolls.
  minWidthPx: number;
}

// The CP code column is a label, not a setting, so it sits outside the list.
export const CP_CODE_COLUMN_WIDTH_PX = 48;
// Matches the `gap-2` on the grid rows.
export const CONDENSED_GRID_GAP_PX = 8;

// Column floors per font-size tier. Side / Direction hold the longest option
// text ("Two-Way", "Withhold") and truncate if squeezed, so their floor is the
// Edit Panel's proven SIDE width (COLUMN_WIDTHS_BY_FONT_SIZE.side, 105-120px)
// for the identical dropdown. The advise columns only hold Yes/No and Iceberg
// only "100 %", so both stay tighter - tight enough that the Awarded-onwards
// four-column row still fits the panel's default width with no scrollbar.
// Same +5px per tier step as the Edit Panel.
const COLUMN_FLOORS_BY_FONT_SIZE: Record<
  FontSizeSetting,
  { side: number; iceberg: number; advise: number }
> = {
  small: { side: 105, iceberg: 60, advise: 62 },
  medium: { side: 110, iceberg: 64, advise: 66 },
  large: { side: 115, iceberg: 68, advise: 70 },
  "x-large": { side: 120, iceberg: 72, advise: 74 },
};

// LQC-2998: Auto Affirm is Yes/No too, so it shares the advise floor.
export function condensedYesNoWidthPx(fontSize: FontSizeSetting): number {
  return (
    COLUMN_FLOORS_BY_FONT_SIZE[fontSize] ?? COLUMN_FLOORS_BY_FONT_SIZE.medium
  ).advise;
}

// Left to right, mirroring the Edit Panel's column order.
export function buildCondensedColumns({
  showSide,
  showDirection,
  fontSize,
}: {
  showSide: boolean;
  showDirection: boolean;
  fontSize: FontSizeSetting;
}): ICondensedColumn[] {
  const floors =
    COLUMN_FLOORS_BY_FONT_SIZE[fontSize] ?? COLUMN_FLOORS_BY_FONT_SIZE.medium;
  const sideColumns: ICondensedColumn[] = [
    ...(showSide
      ? [
          {
            settingField: "quoteType" as CondensedSettingField,
            heading: "Side",
            minWidthPx: floors.side,
          },
        ]
      : []),
    ...(showDirection
      ? [
          {
            settingField: "releaseDirection" as CondensedSettingField,
            heading: "Direction",
            minWidthPx: floors.side,
          },
        ]
      : []),
  ];
  return [
    ...sideColumns,
    {
      settingField: "releaseSize",
      heading: "Iceberg",
      minWidthPx: floors.iceberg,
    },
    {
      settingField: "releaseCompetition",
      heading: "Advise Comp",
      minWidthPx: floors.advise,
    },
    {
      settingField: "releaseCover",
      heading: "Advise Cover",
      minWidthPx: floors.advise,
    },
    {
      settingField: "releaseCoverSpread",
      heading: "Cover Spread",
      minWidthPx: floors.advise,
    },
  ];
}

// `minmax(floor, 1fr)` lets the columns share a wide panel evenly but never
// squash below the floor - the overflow is what drives the horizontal scroll.
export function buildCondensedGridTemplate(
  columns: ICondensedColumn[],
): string {
  const tracks = columns.map((col) => `minmax(${col.minWidthPx}px, 1fr)`);
  return [`${CP_CODE_COLUMN_WIDTH_PX}px`, ...tracks].join(" ");
}

export function condensedGridMinWidthPx(columns: ICondensedColumn[]): number {
  const floors = columns.reduce((sum, col) => sum + col.minWidthPx, 0);
  const gaps = columns.length * CONDENSED_GRID_GAP_PX;
  return CP_CODE_COLUMN_WIDTH_PX + floors + gaps;
}

// A CP quoting One-Way always shows its direction, so it has nothing to
// withhold or release. An unresolved value counts as One-Way - the default.
export function isCpOneWay(
  settings: ITradeSettingsStore,
  cpCode: string,
): boolean {
  const active = (
    resolveSettingForCP(settings, cpCode, "quoteType") as IToggleButton[]
  )?.find((option) => option.active)?.name;
  return active === undefined || active === QuoteType.OneWay;
}

// Direction is only worth a column when at least one CP is withholding it.
export function anyCpWithholdsDirection(
  settings: ITradeSettingsStore,
  cpCodes: string[],
): boolean {
  return cpCodes.some((code) => !isCpOneWay(settings, code));
}

export interface ICondensedColumnLock {
  disabled: boolean;
  // Distinguishes the tooltip so the caller can build a stable id; absent when
  // the cell is disabled silently (Orders) or not disabled at all.
  tooltipKey?: "sideLocked" | "coverSingleCp";
  tooltipMessage?: string;
}

// Why a per-CP dropdown is locked. Iceberg is not routed through here - its
// floor / at-max rules live with the numeric input. Mirrors the Edit Panel:
// Advise Comp / Advise Cover / Cover Spread are silently disabled for Orders.
export function resolveCpColumnLock({
  settingField,
  isOrder,
  lockCoverBySingleCp,
  coverSpreadEditable,
  sideLocked,
  sideLockMessage,
}: {
  settingField: CondensedSettingField;
  isOrder: boolean;
  lockCoverBySingleCp: boolean;
  coverSpreadEditable: boolean;
  sideLocked: boolean;
  sideLockMessage: string;
}): ICondensedColumnLock {
  if (settingField === "quoteType") {
    if (!sideLocked) return { disabled: false };
    return {
      disabled: true,
      tooltipKey: "sideLocked",
      tooltipMessage: sideLockMessage,
    };
  }
  if (settingField === "releaseDirection") return { disabled: false };
  if (isOrder) return { disabled: true };
  if (
    lockCoverBySingleCp &&
    (settingField === "releaseCover" || settingField === "releaseCoverSpread")
  ) {
    return {
      disabled: true,
      tooltipKey: "coverSingleCp",
      tooltipMessage: SINGLE_CP_COVER_TOOLTIP,
    };
  }
  if (settingField === "releaseCoverSpread" && !coverSpreadEditable) {
    return { disabled: true };
  }
  return { disabled: false };
}
